import moment from "moment-timezone";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { DateRangePicker, type DateRange } from "@/components/ui/date_picker";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export type QueueFilters = {
  customerId: string;
  contractNumber: string;
  createdDate: DateRange | undefined;
};

export type InvoiceFilters = {
  customerId: string;
  invoiceNumber: string;
  contractNumber: string;
  invoiceDate: DateRange | undefined;
};

type CustomerOption = { value: string; label: string };

export function QueueFiltersDialog({
  open,
  filters,
  customerOptions,
  onChange,
  onClose,
  onApply,
}: {
  open: boolean;
  filters: QueueFilters;
  customerOptions: CustomerOption[];
  onChange: (filters: QueueFilters) => void;
  onClose: () => void;
  onApply: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(nextOpen: boolean) => { if (!nextOpen) onClose(); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>Staged Invoice Filters</DialogTitle></DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label>Customer</Label>
            <Combobox
              value={filters.customerId}
              onValueChange={(customerId: string) => onChange({ ...filters, customerId })}
              options={customerOptions}
              placeholder="All customers"
              dwimOnChange
              isModal
              virtualizeList
              popoverClassName="w-[360px]"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="queue-contract-number">Contract Number</Label>
            <Input id="queue-contract-number" value={filters.contractNumber} onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange({ ...filters, contractNumber: event.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="button" onClick={onApply}>Apply Filters</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function InvoiceFiltersDialog({
  open,
  filters,
  customerOptions,
  onChange,
  onClose,
  onApply,
}: {
  open: boolean;
  filters: InvoiceFilters;
  customerOptions: CustomerOption[];
  onChange: (filters: InvoiceFilters) => void;
  onClose: () => void;
  onApply: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(nextOpen: boolean) => { if (!nextOpen) onClose(); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>Invoice Filters</DialogTitle></DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label>Customer</Label>
            <Combobox
              value={filters.customerId}
              onValueChange={(customerId: string) => onChange({ ...filters, customerId })}
              options={customerOptions}
              placeholder="All customers"
              dwimOnChange
              isModal
              virtualizeList
              popoverClassName="w-[360px]"
            />
          </div>
          <div className="grid gap-2">
            <Label>Invoice Date</Label>
            <DateRangePicker start={filters.invoiceDate?.from} end={filters.invoiceDate?.to} onChange={(invoiceDate: DateRange | undefined) => onChange({ ...filters, invoiceDate })} fromYear={2020} toYear={moment().add(2, "years").year()} placeholder="All dates" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="invoice-number">Invoice Number</Label>
            <Input id="invoice-number" value={filters.invoiceNumber} onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange({ ...filters, invoiceNumber: event.target.value })} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="invoice-contract-number">Contract Number</Label>
            <Input id="invoice-contract-number" value={filters.contractNumber} onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange({ ...filters, contractNumber: event.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="button" onClick={onApply}>Apply Filters</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
